import React, { useLayoutEffect } from 'react';
import { Calendar, Clock, ChevronRight, Home } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Participant } from './Participants';
import { useAppContext } from '../../Context';

interface TabData {
  name: string;
  count: number;
}

interface BreadcrumbProps {
  current: string;
}

// Interface for each daily annotation summary.
interface AnnotationData {
    date: string;       // e.g., "January 5, 2025"
    status: string;     // e.g., "In Progress", "Under Review", or "Completed"
    cameras: number;
    annotations: number;
}
  
  // Interface for linking a participant to their annotation data.
  interface ParticipantAnnotationData {
    participantID: string;
    participantTitle: string;
    annotationData: AnnotationData[];
  }

function generateDateRange(start: Date, end: Date): string[] {
    const dates: string[] = [];
    const current = new Date(start);
    while (current <= end) {
      // Format the date as "MM-DD-YYYY"
      const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
      dates.push(current.toLocaleDateString('en-US', options));
      // Increment the day
      current.setDate(current.getDate() + 1);
    }
    return dates;
}

function getSampleAnnotationData(date: string): AnnotationData {
    // Define four possible statuses.
    const statuses = ['In Progress', 'Under Review', 'Completed', 'Pending'];
    
    // Use the day of the month to choose a status.
    const day = new Date(date).getDate();
    const status = statuses[day % statuses.length]; // Cycles through four statuses
    
    // Set annotation numbers based on status (modify as needed)
    let annotationsCount: number;
    switch (status) {
      case 'Completed':
        annotationsCount = 10;
        break;
      case 'Under Review':
        annotationsCount = 4;
        break;
      case 'In Progress':
        annotationsCount = 6;
        break;
      case 'Pending':
        annotationsCount = 2;
        break;
      default:
        annotationsCount = 0;
    }
    
    return {
      date,
      status,
      cameras: 2, // Sample value; adjust if needed.
      annotations: annotationsCount
    };
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ current }) => {

    const navigate = useNavigate();

    const handleClick = () => {
        navigate(`/thermal/participants`);
    };

    return (

  <nav className="tw-flex tw-items-center tw-space-x-2 tw-text-gray-700">
    <Home size={20} />
    <span className="tw-text-base tw-font-normal tw-cursor-pointer hover:tw-underline" onClick={handleClick}>Participants</span>
    <span className="tw-text-base tw-font-normal">&gt;</span>
    <span className="tw-text-base tw-font-semibold">{current}</span>
  </nav>
)

};

const Tab: React.FC<{ tab: TabData; isActive: boolean; onClick: () => void }> = ({ tab, isActive, onClick }) => (
    <button
      className={`tw-px-6 tw-py-2 tw-text-sm tw-font-medium tw-rounded-md ${
        isActive
          ? 'tw-bg-white tw-text-black tw-shadow-sm tw-border tw-border-gray-300'
          : 'tw-bg-gray-100 tw-text-gray-600'
      }`}
      onClick={onClick}
    >
      {tab.name} ({tab.count})
    </button>
  );


const AnnotationCard: React.FC<AnnotationData & { participantID: string }> = ({ date, status, cameras, annotations, participantID }) => {
    
    const navigate = useNavigate();
    
    const handleClick = () => {
        navigate(`/thermal/participants/${participantID}/${date}/annotate`);
    };
    
    return (


  <div className="tw-border tw-rounded-lg tw-p-4 tw-shadow-md tw-bg-white tw-text-black tw-cursor-pointer hover:tw-bg-gray-100" onClick={handleClick}>
    <div className="tw-flex tw-justify-between tw-items-center">
      <div className="tw-flex tw-items-center tw-space-x-2 tw-text-gray-500">
        <Calendar size={16} />
        <span>{date}</span>
        <span className="tw-px-2 tw-py-1 tw-rounded-full tw-text-sm tw-font-medium tw-bg-black tw-text-white">
            {status}
        </span>
      </div>
      <div className="tw-flex tw-items-center tw-mt-4 tw-text-sm tw-text-gray-600">
        <span className="tw-mx-4">📸 {cameras} sensors</span>
        <span>{annotations} annotations</span>
      </div>
    </div>
    
  </div>
)
};

const ParticipantDetailPage: React.FC = () => {
  const [tabs, setTabs] = React.useState<TabData[]>([]);

  const appContext = useAppContext();

  useLayoutEffect(() => {
    appContext.setFullScreen(false);
  }, []);

  const [activeTab, setActiveTab] = React.useState("In Progress");

  const participants: Participant[] = [
    {
      id: 'p1',
      title: 'Matthew S.',
      duration: '3 days',
      lastUpdated: '5d ago',
      progress: 22,
      status: 'active',
      annotations: [],
    },
    // Insert new participant at index 1
    {
      id: 'p_new1',
      title: 'Jamie P.',
      duration: '4 days',
      lastUpdated: '4d ago',
      progress: 30,
      status: 'active',
      annotations: [],
    },
    // Insert new participant at index 2
    {
      id: 'p_new2',
      title: 'Jordan W.',
      duration: '2 days',
      lastUpdated: '7d ago',
      progress: 40,
      status: 'active',
      annotations: [],
    },
    {
      id: 'p2',
      title: 'Daylan G.',
      duration: '3 days',
      lastUpdated: '5d ago',
      progress: 47,
      status: 'active',
      annotations: [],
    },
    {
      id: 'p3',
      title: 'John R.',
      duration: '2 days',
      lastUpdated: '6d ago',
      progress: 35,
      status: 'active',
      annotations: [],
    },
    {
      id: 'p4',
      title: 'Yolanda J.',
      duration: '3 days',
      lastUpdated: '5d ago',
      progress: 18,
      status: 'active',
      annotations: [],
    },
    {
      id: 'p5',
      title: 'Chelsea J.',
      duration: '3 days',
      lastUpdated: '5d ago',
      progress: 29,
      status: 'active',
      annotations: [],
    },
    {
      id: 'p6',
      title: 'Robert K.',
      duration: '2 days',
      lastUpdated: '6d ago',
      progress: 50,
      status: 'active',
      annotations: [],
    },
    {
      id: 'p7',
      title: 'Kaelah B.',
      duration: '2 days',
      lastUpdated: '6d ago',
      progress: 14,
      status: 'active',
      annotations: [],
    },
    {
      id: 'p8',
      title: 'Alexander B.',
      duration: '2 days',
      lastUpdated: '6d ago',
      progress: 40,
      status: 'active',
      annotations: [],
    },
    {
      id: 'p9',
      title: 'Brent S.',
      duration: '2 days',
      lastUpdated: '6d ago',
      progress: 33,
      status: 'active',
      annotations: [],
    },
    {
      id: 'p10',
      title: 'John P. K.',
      duration: '2 days',
      lastUpdated: '6d ago',
      progress: 10,
      status: 'active',
      annotations: [],
    },
    {
      id: 'p11',
      title: 'Brad M.',
      duration: '2 days',
      lastUpdated: '6d ago',
      progress: 55,
      status: 'active',
      annotations: [],
    },
    {
      id: 'p12',
      title: 'Jopseph D.',
      duration: '2 days',
      lastUpdated: '6d ago',
      progress: 27,
      status: 'active',
      annotations: [],
    },
    {
      id: 'p13',
      title: 'Emma A.',
      duration: '3 days',
      lastUpdated: '5d ago',
      progress: 38,
      status: 'active',
      annotations: [],
    },
    {
      id: 'p14',
      title: 'Michael F.',
      duration: '3 days',
      lastUpdated: '5d ago',
      progress: 44,
      status: 'active',
      annotations: [],
    },
    {
      id: 'p15',
      title: 'Mickael R.',
      duration: '2 days',
      lastUpdated: '6d ago',
      progress: 31,
      status: 'active',
      annotations: [],
    },
  ];

  const startDate = new Date('2025-01-05T00:00:00');
    const endDate = new Date('2025-01-12T00:00:00');
    const dateRange = generateDateRange(startDate, endDate);

  const storedAnnotations: ParticipantAnnotationData[] = participants.map(p => {
    // Generate annotation data for each date in the range.
    const annotationData: AnnotationData[] = dateRange.map(date => getSampleAnnotationData(date));
    
    return {
      participantID: p.id,
      participantTitle: p.title,
      annotationData
    };
  });

  const participantID = useParams().participantID;

  const [annotations, setAnnotations] = React.useState<AnnotationData[]>([]);

  const [participant, setParticipant] = React.useState<Participant | null>(null);

  React.useEffect(() => {
    const participantAnnotations = storedAnnotations.find(pa => pa.participantID === participantID);
    if (participantAnnotations) {
      setAnnotations(participantAnnotations.annotationData);
      // Remove duplicates based on status
      const uniqueAnnotations = Array.from(new Map(participantAnnotations.annotationData.map(a => [a.status, a])).values());

     // hard code the statuses
     const statuses = ['Pending', 'In Progress', 'Under Review', 'Completed'];

     const statusCounts = statuses.map(status => ({
      name: status,
      count: participantAnnotations.annotationData.filter(a => a.status === status).length
     }));

     console.log(statusCounts);
      setTabs(statusCounts);

      console.log(participantAnnotations.annotationData);

    }

    const participant = participants.find(p => p.id === participantID);
    if (participant) {
      setParticipant(participant);
    }
  }, [participantID]);

  return (
    <div className="tw-min-h-screen tw-bg-white tw-p-6 tw-text-black">
      <Breadcrumb current={participant?.title || ''} />
      <header className="tw-mt-6">
        <h1 className="tw-text-2xl tw-font-bold">{participant?.title || ''}</h1>
        <p className="tw-text-gray-600">Started {participant?.duration || ''}</p>
      </header>
      <div className="tw-w-full tw-grid tw-grid-cols-4 tw-space-x-2 tw-mt-4">
        {tabs.map((tab, index) => (
          <Tab
            key={index}
            tab={tab}
            isActive={tab.name === activeTab}
            onClick={() => setActiveTab(tab.name)}
          />
        ))}
      </div>
      <div className="tw-mt-6 tw-space-y-4">
        {annotations
          .filter((annotation) => annotation.status === activeTab)
          .map((annotation, index) => (
            <AnnotationCard key={index} {...annotation} participantID={participantID} />
          ))}
      </div>
    </div>
  );
};

export default ParticipantDetailPage;
